// VIS-A1: stagger sibling 60s polling hooks to prevent a synchronized
// thundering-herd of DB chains on every minute boundary AND on every tab
// refocus after a hidden period.
//
// useAutoBump, useOptiFinishPushback, and useConvertedRolloverGrowth all
// mount together (typically from the dashboard / scheduling board), all
// run on a 60s setInterval, and all early-return when `document.hidden`.
// Without staggering, their setInterval timers share a phase (mount-time
// alignment) and fire within milliseconds of each other:
//
//   - Steady state: 3 simultaneous shop_profile + tickets + cascade chains
//     every 60s.
//   - After hidden→visible transition: the next tick of all three fires at
//     the same wall-clock instant, hitting the DB with 3 parallel heavy
//     reads (and up to 3 parallel `triggerBoardCascade` calls) plus
//     overlapping with any Supabase realtime channel reconnects that fire
//     at the same moment.
//
// Staggering injects a deterministic phase offset per sibling so the steady-
// state ticks land 20s apart and never co-occur. Boot eagerness is
// preserved — callers still invoke the function once on mount; only the
// recurring tick is offset.

/**
 * Canonical phase offsets for the VIS-A1 stagger trio. Held together in one
 * place so a future hook can pick a non-overlapping slot or a regression
 * (two siblings reusing the same offset) is obvious from a single grep.
 *
 * Offsets are spaced 20s apart on a 60s cycle: AutoBump at phase 0,
 * OptiFinishPushback at phase 20s, ConvertedRolloverGrowth at phase 40s.
 * After the startup delay elapses each sibling's setInterval anchors to a
 * different point in the 60s cycle and stays there until unmount.
 */
export const VIS_A1_STAGGER = {
  AUTO_BUMP: 0,
  OPTI_FINISH_PUSHBACK: 20_000,
  CONVERTED_ROLLOVER_GROWTH: 40_000,
} as const;

/**
 * Schedule a recurring callback whose first invocation is delayed by
 * `startupDelayMs`, then repeats every `intervalMs`. Returns a single
 * cleanup function that clears both the deferred startup and the eventual
 * interval — safe to call before the startup timer has fired.
 *
 * The helper does NOT call `fn` immediately. Callers that want eager boot
 * behavior should invoke `fn()` once before scheduling, exactly as the
 * three VIS-A1 sibling hooks do.
 */
export function createStaggeredInterval(
  fn: () => void,
  intervalMs: number,
  startupDelayMs: number,
): () => void {
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let startupId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    startupId = null;
    intervalId = setInterval(fn, intervalMs);
  }, startupDelayMs);

  return () => {
    if (startupId !== null) {
      clearTimeout(startupId);
      startupId = null;
    }
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}
